import { useEffect, useState } from 'react';

import useUserStore from '../../store/user';
import usePresenterMode from '../../hooks/usePresenterMode';
import { promoteRolePreviewOverrideFromUrl } from '../../utils/previewOverrideUrl';
import { ROLE_PREVIEW_OVERRIDE_EVENT, getRolePreviewOverride } from './rolePreviewOverride';
import { ROLES, parseRole, type Role } from './roles';

export function useRealRole(): Role | null {
  const user = useUserStore((s) => s.user);
  return parseRole(user?.activeRole?.name);
}

/**
 * Whether the account really is Nexoya support, per the access token.
 *
 * The one role predicate that reads `useRealRole` instead of `useEffectiveRole`, because
 * it is what authorises the runtime downgrades themselves. `useIsSupport` cannot be used
 * for that: it reads the EFFECTIVE role, so the preview override would be authorising
 * its own bypass — append `?preview_role=support` and every gate in the app opens.
 *
 * Not a security boundary either way; the API enforces the real role carried by the
 * token. This keeps a debug affordance from *looking* like a privilege the UI grants.
 */
export function useIsRealSupport(): boolean {
  return useRealRole() === ROLES.SUPPORT;
}

/**
 * The support-only debug override, live. `null` for everyone who is not really support.
 *
 * `useSyncExternalStore` would be idiomatic, but this repo's `@types/react` is pinned at
 * 17 while React 18.3 is installed, so it does not type-check.
 */
export function useRolePreviewOverride(): Role | null {
  const isRealSupport = useIsRealSupport();
  const [override, setOverride] = useState<Role | null>(getRolePreviewOverride);

  useEffect(() => {
    // Ignore both sources outright for anyone else — no read, and above all no promote,
    // which would persist a stranger's URL param into their own storage.
    if (!isRealSupport) return;

    const sync = () => {
      // Consume the URL param first so localStorage is the single live source
      // afterwards; otherwise the override can never be switched off. It is a no-op
      // once the param is gone, so re-running it per event is free. See the function's
      // own comment.
      promoteRolePreviewOverrideFromUrl();
      setOverride(getRolePreviewOverride());
    };
    // Re-read once on mount: the value can change between first render and subscribing.
    sync();
    window.addEventListener('storage', sync);
    window.addEventListener(ROLE_PREVIEW_OVERRIDE_EVENT, sync);
    // Back/forward can land on an older entry that still carries the param — only the
    // entry we replaced was cleaned — so promote and re-resolve on `popstate` too.
    // `useLocation()` would be the obvious alternative and is not usable: this hook has
    // to work outside a Router (Storybook, unit tests).
    window.addEventListener('popstate', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(ROLE_PREVIEW_OVERRIDE_EVENT, sync);
      window.removeEventListener('popstate', sync);
    };
    // Re-runs when the user store resolves: the role is null on the very first paint.
  }, [isRealSupport]);

  // Guarded on render, not just in the effect, so a non-support user never gets even one
  // frame of somebody else's seat out of the `useState` initialiser.
  return isRealSupport ? override : null;
}

/**
 * The role every permission check reads.
 *
 * Precedence, highest first:
 *   1. presenter mode   — a product behaviour whose guarantee is that the UI always looks
 *                         read-only. A debug tool must not be able to break that.
 *   2. preview override — gated on the REAL role being support (`useRolePreviewOverride`
 *                         returns null for everyone else), and PRESENTATION ONLY: the API
 *                         still enforces the real role carried by the access token. It can
 *                         also only ever downgrade — see `PREVIEWABLE_ROLES`.
 *   3. the real role.
 */
export function useEffectiveRole(): Role | null {
  const { isPresenterMode } = usePresenterMode();
  const override = useRolePreviewOverride();
  const real = useRealRole();
  if (isPresenterMode) return ROLES.READONLY;
  return override ?? real;
}

export function useIsSupport(): boolean {
  return useEffectiveRole() === ROLES.SUPPORT;
}

export function useIsMember(): boolean {
  return useEffectiveRole() === ROLES.MEMBER;
}

export function useIsReadOnly(): boolean {
  return useEffectiveRole() === ROLES.READONLY;
}

export function useIsAdmin(): boolean | null {
  const effectiveRole = useEffectiveRole();
  return effectiveRole ? effectiveRole === ROLES.ADMIN : null;
}
